import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import { FilterDropdownComponent } from '../filter-dropdown/filter-dropdown.component';

export type SearchBarOption = {
  id: number;
  label: string;
};

export type SearchFilters = {
  bookingForId: number | null;
  locationId: number | null;
  floorIds: number[];
  periodId: number | null;
  startDate: Date | null;
  repeatEnabled: boolean;
  endDate: Date | null;
  selectedDays: WeekdayKey[];
};

type SearchErrors = {
  locationError: boolean;
  floorsError: boolean;
  periodError: boolean;
  startDateError: boolean;
  endDateError: boolean;
  endBeforeStartError: boolean;
  repeatDaysError: boolean;
  repeatRangeError: boolean;
};

type DateSearchErrors = Pick<
  SearchErrors,
  | 'periodError'
  | 'startDateError'
  | 'endDateError'
  | 'endBeforeStartError'
  | 'repeatDaysError'
  | 'repeatRangeError'
>; // this is here because the date search filter has multiple error types

type WeekdayKey = 'M' | 'T' | 'W' | 'R' | 'F'; // T = Tuesday, R = Thursday

@Component({
  standalone: false,
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent implements OnChanges {
  @HostBinding('class.keyboard-nav') keyboardNav: boolean = false;
  @ViewChild('dateDropdown') dateDropdown?: FilterDropdownComponent;
  @ViewChild('locationSelect') locationSelect?: MatSelect;
  @ViewChild('floorSelect') floorSelect?: MatSelect;

  @Output() readonly bookingForChanged = new EventEmitter<number | null>();
  @Output() readonly locationChanged = new EventEmitter<number | null>();
  @Output() readonly floorSelectionChanged = new EventEmitter<number[]>();
  @Output() readonly filtersChanged = new EventEmitter<Date | null>();
  @Output() readonly searchRequested = new EventEmitter<SearchFilters>();

  @Input() bookingForOptions: SearchBarOption[] = [];
  @Input() locations: SearchBarOption[] = [];
  @Input() floors: SearchBarOption[] = [];
  @Input() timePeriods: SearchBarOption[] = [];

  @Input() selectedLocationId: number | null = -1;
  @Input() selectedFloorIds: number[] = [-1];
  @Input() selectedPeriodId: number | null = null;
  @Input() selectedStartDate: Date | null = null;
  @Input() selectedEndDate: Date | null = null;
  @Input() selectedBookingForId: number = 1;
  @Input() selectedBookingForLabel: string = '';
  @Input() bookingForSelfOptionLabel: string = 'Myself';
  @Input() bookingForNoEntriesLabel: string = 'No Results';
  @Input() bookingForSearchControl: UntypedFormControl = new UntypedFormControl('');
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() autofillInProgress: boolean = false;
  @Input() repeatDisabled: boolean = false;
  @Input() repeatEnabled: boolean = false;

  @Output() readonly repeatEnabledChanged = new EventEmitter<boolean>();
  @Output() readonly selectedDaysChanged = new EventEmitter<Record<WeekdayKey, boolean>>();
  @Output() readonly periodChanged = new EventEmitter<number | null>();
  @Output() readonly startDateChanged = new EventEmitter<Date | null>();
  @Output() readonly endDateChanged = new EventEmitter<Date | null>();

  showValidation: boolean = false;

  readonly weekdays: WeekdayKey[] = ['M', 'T', 'W', 'R', 'F'];
  selectedDays: Record<WeekdayKey, boolean> = {
    M: false,
    T: false,
    W: false,
    R: false,
    F: false,
  };

  searchErrors: SearchErrors = {
    locationError: false,
    floorsError: false,
    periodError: false,
    startDateError: false,
    endDateError: false,
    endBeforeStartError: false,
    repeatDaysError: false,
    repeatRangeError: false,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedLocationId']) {
      this.selectedLocationId =
        this.selectedLocationId == null || this.selectedLocationId === -1
          ? -1
          : this.selectedLocationId;
    }

    if (changes['selectedFloorIds']) {
      const normalizedIncoming = (this.selectedFloorIds ?? []).filter((id) => id !== -1);
      this.selectedFloorIds = normalizedIncoming.length > 0 ? normalizedIncoming : [-1];
    }

    if (
      changes['bookingForOptions'] &&
      this.bookingForOptions.length > 0 &&
      this.selectedBookingForId == null
    ) {
      this.selectedBookingForId = this.bookingForOptions[0].id;
    }

    if (changes['selectedPeriodId']) {
      this.selectedPeriodId = this.selectedPeriodId ?? null;

      if (
        this.selectedPeriodId != null &&
        this.timePeriods.length > 0 &&
        !this.timePeriods.some((p) => p.id === this.selectedPeriodId)
      ) {
        this.selectedPeriodId = null;
      }
    }

    if (changes['timePeriods'] && this.timePeriods.length > 0) {
      const hasSelected =
        this.selectedPeriodId != null &&
        this.timePeriods.some((p) => p.id === this.selectedPeriodId);
      if (!hasSelected) {
        this.selectedPeriodId = null;
      }
    }

    if (changes['locations'] && this.locations.length > 0) {
      const hasSelected = this.locations.some((l) => l.id === this.selectedLocationId);
      if (!hasSelected) {
        this.selectedLocationId = -1;
      }
    }

    if (changes['floors']) {
      if (this.floors.length > 0) {
        const validIds = new Set(this.floors.map((f) => f.id));
        const selected = this.selectedFloorIds.filter((id) => validIds.has(id));
        this.selectedFloorIds = selected.length > 0 ? selected : [-1];
      } else {
        this.selectedFloorIds = [-1];
      }
    }

    if (changes['repeatDisabled'] && this.repeatDisabled && this.repeatEnabled) {
      this.onRepeatChange(false);
    }

    this.refreshValidationState();
  }

  get locationText(): string {
    if (this.selectedLocationId === -1) {
      return 'Select a location';
    }

    return this.lookupLabel(this.locations, this.selectedLocationId, '');
  }

  get isLocationEmpty(): boolean {
    return this.selectedLocationId === -1;
  }

  get isLocationDisabled(): boolean {
    return this.autofillInProgress;
  }

  get isFloorsDisabled(): boolean {
    return this.selectedLocationId === -1 || this.autofillInProgress;
  }

  get isTimePeriodDisabled(): boolean {
    return this.autofillInProgress;
  }

  get bookingForText(): string {
    const selectedOption = this.bookingForOptions.find(
      (option) => option.id === this.selectedBookingForId
    );

    if (selectedOption) {
      return this.bookingForTriggerText(selectedOption);
    }

    return this.selectedBookingForLabel || 'Myself';
  }

  get sortedBookingForOptions(): SearchBarOption[] {
    const options: SearchBarOption[] = [...(this.bookingForOptions ?? [])];
    return options.sort((a, b) => {
      const aIsMyself: boolean = this.isMyselfLabel(a.label);
      const bIsMyself: boolean = this.isMyselfLabel(b.label);

      if (aIsMyself && !bIsMyself) {
        return -1;
      }

      if (!aIsMyself && bIsMyself) {
        return 1;
      }

      return (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' });
    });
  }

  get needsSelectedBookingForGhostOption(): boolean {
    return (
      this.selectedBookingForId != null &&
      !this.bookingForOptions.some((option) => option.id === this.selectedBookingForId)
    );
  }

  onBookingForChange(value: number | null): void {
    this.selectedBookingForId = value ?? 1;
    this.bookingForChanged.emit(this.selectedBookingForId);
    this.emitFiltersChanged();
  }

  onBookingForPanelOpenChange(isOpen: boolean): void {
    if (!isOpen) {
      this.bookingForSearchControl.setValue('', { emitEvent: true });
    }
  }

  onBookingForSearchEnter(event: KeyboardEvent, bookingForSelect: MatSelect): void {
    event.preventDefault();
    event.stopPropagation();

    const optionValue = this.getActiveBookingForOptionValue(bookingForSelect);
    if (optionValue == null) {
      return;
    }

    this.onBookingForChange(optionValue);
    bookingForSelect.close();
    bookingForSelect.focus();
  }

  bookingForOptionText(option: SearchBarOption): string {
    return this.isMyselfLabel(option?.label)
      ? this.bookingForSelfOptionLabel
      : (option?.label ?? '');
  }

  bookingForTriggerText(option: SearchBarOption): string {
    return this.isMyselfLabel(option?.label) ? 'Myself' : (option?.label ?? '');
  }

  onLocationSelectionChange(value: number | null): void {
    this.selectedLocationId = value == null || value === -1 ? -1 : value;

    if (this.selectedLocationId === -1) {
      this.selectedFloorIds = [-1];
      this.floorSelectionChanged.emit([]);
    }

    this.locationChanged.emit(this.selectedLocationId === -1 ? null : this.selectedLocationId);

    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  get floorText(): string {
    const selectedRealFloorIds: number[] = this.selectedFloorIds.filter((id) => id !== -1);
    const selectedLabels: string[] = this.floors
      .filter((floor) => selectedRealFloorIds.includes(floor.id))
      .map((floor) => floor.label);

    if (selectedLabels.length === 0) {
      return 'Select a floor';
    }

    if (selectedLabels.length === 1) {
      return selectedLabels[0];
    }

    return `${selectedLabels[0]} +${selectedLabels.length - 1}`;
  }

  get isFloorEmpty(): boolean {
    return this.selectedFloorIds.filter((id) => id !== -1).length === 0;
  }

  onFloorSelectionChange(value: number[] | null): void {
    const normalized: number[] = Array.isArray(value) ? value.filter((id) => id !== -1) : [];
    this.selectedFloorIds = normalized.length > 0 ? normalized : [-1];
    this.floorSelectionChanged.emit(normalized);
    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  get dateText(): string {
    if (!this.selectedStartDate) {
      return 'Select a date';
    }

    return this.formatDate(this.selectedStartDate);
  }

  get isDateEmpty(): boolean {
    return !this.selectedStartDate;
  }

  openDatepicker(datepicker: MatDatepicker<Date>): void {
    if (datepicker.opened) {
      return;
    }

    setTimeout(() => {
      if (!datepicker.opened) {
        datepicker.open();
      }
    });
  }

  openDatepickerOnEnter(event: KeyboardEvent, datepicker: MatDatepicker<Date>): void {
    event.preventDefault();
    this.openDatepicker(datepicker);
  }

  onRepeatChange(checked: boolean): void {
    if (this.repeatDisabled && checked) {
      return;
    }

    this.repeatEnabled = checked;
    this.repeatEnabledChanged.emit(checked);

    if (!checked) {
      this.endDateChanged.emit(null);
      this.selectedDays = {
        M: false,
        T: false,
        W: false,
        R: false,
        F: false,
      };

      this.selectedDaysChanged.emit(this.selectedDays);
    }

    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  onSearchButtonPointerDown(event: PointerEvent): void {
    const button: HTMLElement = event.currentTarget as HTMLElement | null;
    if (!button) {
      return;
    }

    const rect: DOMRect = button.getBoundingClientRect();
    const x: number = event.clientX - rect.left;
    const y: number = event.clientY - rect.top;

    button.style.setProperty('--ods-button-ripple-x', `${x}px`);
    button.style.setProperty('--ods-button-ripple-y', `${y}px`);

    button.classList.remove('ripple-active');
    void button.offsetWidth;
    button.classList.add('ripple-active');

    setTimeout(() => {
      button.classList.remove('ripple-active');
    }, 420);
  }

  onSearchClick(): void {
    this.showValidation = true;
    this.refreshValidationState();
    if (this.hasErrors) {
      this.focusFirstInvalidControl();
      return;
    }

    const selectedDays: WeekdayKey[] = this.weekdays.filter((day) => this.selectedDays[day]);
    const floorIds: number[] = this.selectedFloorIds.filter((id) => id !== -1);

    this.searchRequested.emit({
      bookingForId: this.selectedBookingForId,
      locationId: this.selectedLocationId === -1 ? null : this.selectedLocationId,
      floorIds,
      periodId: this.selectedPeriodId,
      startDate: this.selectedStartDate,
      repeatEnabled: this.repeatEnabled,
      endDate: this.selectedEndDate,
      selectedDays,
    });
  }

  toggleDay(day: WeekdayKey): void {
    this.selectedDays[day] = !this.selectedDays[day];
    this.selectedDaysChanged.emit(this.selectedDays);
    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  isDayActive(day: WeekdayKey): boolean {
    return !!this.selectedDays[day];
  }

  getWeekdayLabel(day: WeekdayKey): string {
    return day === 'R' ? 'T' : day;
  }

  onStartDateChanged(): void {
    this.startDateChanged.emit(this.selectedStartDate);
    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  onEndDateChanged(): void {
    this.endDateChanged.emit(this.selectedEndDate);
    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  onPeriodChanged(): void {
    this.periodChanged.emit(this.selectedPeriodId);
    this.refreshValidationState();
    this.emitFiltersChanged();
  }

  private emitFiltersChanged(): void {
    this.filtersChanged.emit(this.selectedStartDate);
  }

  readonly weekdayFilter = (date: Date | null): boolean => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return false;
    }

    const candidate: Date = this.toDateOnly(date);
    const min: Date = this.toDateOnly(this.minDate);
    const max: Date = this.toDateOnly(this.maxDate);
    if (candidate == null) {
      return false;
    }

    if (min != null && candidate < min) {
      return false;
    }

    if (max != null && candidate > max) {
      return false;
    }

    const day: number = date.getDay();
    return day !== 0 && day !== 6;
  };

  get hasErrors(): boolean {
    return this.hasAnyError(this.searchErrors);
  }

  get hasDateErrors(): boolean {
    const dateErrors: DateSearchErrors = {
      periodError: this.searchErrors.periodError,
      startDateError: this.searchErrors.startDateError,
      endDateError: this.searchErrors.endDateError,
      endBeforeStartError: this.searchErrors.endBeforeStartError,
      repeatDaysError: this.searchErrors.repeatDaysError,
      repeatRangeError: this.searchErrors.repeatRangeError,
    };

    return this.hasAnyError(dateErrors);
  }

  get dateValidationMessages(): string[] {
    const messages: string[] = [];

    if (this.searchErrors.periodError) {
      messages.push('Select a time period');
    }

    if (this.searchErrors.startDateError) {
      messages.push('Select a valid start date');
    }

    if (this.searchErrors.endDateError) {
      messages.push('Select a valid end date');
    }

    if (this.searchErrors.endBeforeStartError) {
      messages.push('End date cannot be before start date');
    }

    if (this.searchErrors.repeatDaysError) {
      messages.push('Select at least one recurrence day');
    }

    if (this.searchErrors.repeatRangeError) {
      messages.push('The reservation does not contain any valid dates');
    }

    return messages;
  }

  get dateSummaryErrorIds(): string | null {
    if (!this.showValidation || !this.hasDateErrors || !!this.dateDropdown?.isOpen) {
      return null;
    }

    return this.dateValidationMessages.map((_, index) => `date-summary-error-${index}`).join(' ');
  }

  get repeatErrorsDescribedBy(): string | null {
    const ids: string[] = [];

    if (this.showValidation && this.searchErrors.repeatDaysError) {
      ids.push('repeat-days-error');
    }

    if (this.showValidation && this.searchErrors.repeatRangeError) {
      ids.push('repeat-range-error');
    }

    return ids.length > 0 ? ids.join(' ') : null;
  }

  get endDateErrorDescribedBy(): string | null {
    const ids: string[] = [];

    if (this.showValidation && this.searchErrors.endDateError) {
      ids.push('end-date-error');
    }

    if (this.showValidation && this.searchErrors.endBeforeStartError) {
      ids.push('end-before-start-error');
    }

    return ids.length > 0 ? ids.join(' ') : null;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const key: string = event.key;
    if (key === 'Tab' || key === 'Enter' || key.startsWith('Arrow')) {
      this.keyboardNav = true;
    }
  }

  @HostListener('document:mousedown')
  onDocumentMouseDown(): void {
    this.keyboardNav = false;
  }

  @HostListener('document:touchstart')
  onDocumentTouchStart(): void {
    this.keyboardNav = false;
  }

  private clearDays(): void {
    this.weekdays.forEach((day) => {
      this.selectedDays[day] = false;
    });

    this.selectedDaysChanged.emit(this.selectedDays);
  }

  refreshValidationState(): void {
    if (!this.showValidation) {
      this.resetErrors();
      return;
    }

    const selectedDays: WeekdayKey[] = this.weekdays.filter((day) => this.selectedDays[day]);
    const selectedFloorIds: number[] = this.selectedFloorIds.filter((id) => id !== -1);
    const startDate: Date = this.toDateOnly(this.selectedStartDate);
    const endDate: Date = this.toDateOnly(this.selectedEndDate);
    const repeatActive: boolean = this.repeatEnabled && !this.repeatDisabled;

    this.searchErrors.locationError = this.selectedLocationId === -1;
    this.searchErrors.floorsError = selectedFloorIds.length === 0;
    this.searchErrors.periodError = this.selectedPeriodId == null || this.selectedPeriodId <= 0;
    this.searchErrors.startDateError =
      startDate == null || !this.isWeekday(startDate) || !this.isInsideWindow(startDate);

    const endDateMissing: boolean = repeatActive && endDate == null;
    const endDateNotWeekday: boolean = repeatActive && endDate != null && !this.isWeekday(endDate);
    const endDateOutsideWindow: boolean =
      repeatActive && endDate != null && !this.isInsideWindow(endDate);
    this.searchErrors.endDateError = endDateMissing || endDateNotWeekday || endDateOutsideWindow;

    this.searchErrors.endBeforeStartError =
      repeatActive && startDate != null && endDate != null && endDate < startDate;
    this.searchErrors.repeatDaysError = repeatActive && selectedDays.length === 0;
    this.searchErrors.repeatRangeError =
      repeatActive &&
      startDate != null &&
      endDate != null &&
      selectedDays.length > 0 &&
      !this.hasDayOverlap(startDate, endDate, selectedDays);
  }

  private resetErrors(): void {
    this.searchErrors = {
      locationError: false,
      floorsError: false,
      periodError: false,
      startDateError: false,
      endDateError: false,
      endBeforeStartError: false,
      repeatDaysError: false,
      repeatRangeError: false,
    };
  }

  private hasAnyError(errors: SearchErrors | DateSearchErrors): boolean {
    return Object.values(errors).some(Boolean);
  }

  private focusFirstInvalidControl(): void {
    if (this.searchErrors.locationError) {
      this.locationSelect?.focus();
      return;
    }

    if (this.hasDateErrors) {
      this.dateDropdown?.focusTrigger();
      return;
    }

    if (this.searchErrors.floorsError) {
      this.floorSelect?.focus();
    }
  }

  private toDateOnly(value: Date | null): Date | null {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }

    const normalized: Date = new Date(value);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private isWeekday(value: Date): boolean {
    const day: number = value.getDay();
    return day !== 0 && day !== 6;
  }

  private isInsideWindow(value: Date): boolean {
    const min: Date = this.toDateOnly(this.minDate);
    const max: Date = this.toDateOnly(this.maxDate);

    if (min != null && value < min) {
      return false;
    }

    if (max != null && value > max) {
      return false;
    }

    return true;
  }

  private hasDayOverlap(start: Date, end: Date, selectedDays: WeekdayKey[]): boolean {
    if (end < start) {
      return false;
    }

    const dayMap: Record<WeekdayKey, number> = {
      M: 1,
      T: 2,
      W: 3,
      R: 4,
      F: 5,
    };

    const activeWeekdays: Set<number> = new Set(selectedDays.map((key) => dayMap[key]));
    const current: Date = new Date(start);

    while (current <= end) {
      if (activeWeekdays.has(current.getDay())) {
        return true;
      }

      current.setDate(current.getDate() + 1);
    }

    return false;
  }

  private lookupLabel(items: SearchBarOption[], id: number | null, fallback: string): string {
    if (id == null) {
      return fallback;
    }

    const match: SearchBarOption = items.find((item) => item.id === id);
    return match ? match.label : fallback;
  }

  private isMyselfLabel(label: string | null | undefined): boolean {
    return (label ?? '').trim().toLowerCase() === 'myself';
  }

  private getActiveBookingForOptionValue(bookingForSelect: MatSelect): number | null {
    const panel: HTMLElement | null = bookingForSelect.panel?.nativeElement ?? null;
    if (!panel) {
      return null;
    }

    const activeOptionElement =
      panel.querySelector<HTMLElement>(
        '.mat-mdc-option.mat-mdc-option-active:not(.booking-for-search-option), .mat-mdc-option.mdc-list-item--activated:not(.booking-for-search-option)'
      ) ??
      Array.from(
        panel.querySelectorAll<HTMLElement>('.mat-mdc-option:not(.booking-for-search-option)')
      ).find((option) => this.isVisibleBookingForOption(option)) ??
      null;

    if (!activeOptionElement?.id) {
      return null;
    }

    const activeOption = bookingForSelect.options
      .toArray()
      .find((option) => option.id === activeOptionElement.id && !option.disabled);

    return typeof activeOption?.value === 'number' ? activeOption.value : null;
  }

  private isVisibleBookingForOption(option: HTMLElement): boolean {
    return option.getAttribute('aria-hidden') !== 'true' && option.getClientRects().length > 0;
  }

  private formatDate(value: Date): string {
    return value.toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
